import { cva, type VariantProps } from "class-variance-authority";

import { Skeleton } from "@/shared/components/primitives/skeleton";
import { cn } from "@/shared/lib/utils";

// ─── Base ─────────────────────────────────────────────────────────────────────

const skeletonVariants = cva("", {
  variants: {
    shape: {
      rectangle: "rounded-md",
      circle: "rounded-full",
    },
  },
  defaultVariants: { shape: "rectangle" },
});

interface TenkoSkeletonProps extends VariantProps<typeof skeletonVariants> {
  className?: string;
}

/**
 * Base skeleton loader with shape variants. Use directly for custom placeholder shapes; prefer the named
 * variants (`TenkoButtonSkeleton`, `TenkoAvatarSkeleton`, etc.) for common UI patterns.
 *
 * @summary Animated placeholder for loading states.
 */
export function TenkoSkeleton({ shape, className }: TenkoSkeletonProps) {
  return <Skeleton className={cn(skeletonVariants({ shape }), className)} />;
}

// ─── Button ───────────────────────────────────────────────────────────────────

const buttonSkeletonVariants = cva("w-24 rounded-md", {
  variants: {
    size: {
      xs: "h-6",
      sm: "h-8",
      default: "h-9",
      lg: "h-10",
    },
  },
  defaultVariants: { size: "default" },
});

interface TenkoButtonSkeletonProps extends VariantProps<typeof buttonSkeletonVariants> {
  className?: string;
}

/**
 * Skeleton placeholder that matches `TenkoButton` dimensions. Pass the same `size` used on the target button
 * so the placeholder holds the exact space.
 *
 * @summary Button-shaped loading placeholder, matches TenkoButton sizes.
 */
export function TenkoButtonSkeleton({ size, className }: TenkoButtonSkeletonProps) {
  return <Skeleton className={cn(buttonSkeletonVariants({ size }), className)} />;
}

// ─── Line ─────────────────────────────────────────────────────────────────────

const lineSkeletonVariants = cva("h-4 rounded-md", {
  variants: {
    width: {
      full: "w-full",
      "3/4": "w-3/4",
      "1/2": "w-1/2",
      "1/3": "w-1/3",
    },
  },
  defaultVariants: { width: "full" },
});

interface TenkoLineSkeletonProps extends VariantProps<typeof lineSkeletonVariants> {
  /**
   * Number of stacked lines to render. When greater than 1 the last line is automatically narrower (`w-1/2`)
   * to mimic a natural paragraph ending.
   */
  lines?: number;
  className?: string;
}

/**
 * Skeleton placeholder for one or more lines of text. Multi-line mode stacks lines with a gap and shortens
 * the last one automatically.
 *
 * @summary Text-line loading placeholder, supports multi-line paragraphs.
 */
export function TenkoLineSkeleton({ lines = 1, width, className }: TenkoLineSkeletonProps) {
  if (lines === 1) {
    return <Skeleton className={cn(lineSkeletonVariants({ width }), className)} />;
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={lineSkeletonVariants({ width: i === lines - 1 ? "1/2" : width })} />
      ))}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const avatarSkeletonVariants = cva("shrink-0", {
  variants: {
    size: {
      xs: "size-5",
      sm: "size-6",
      default: "size-8",
      lg: "size-10",
    },
    shape: {
      circle: "rounded-full",
      square: "rounded-[25%]",
    },
  },
  defaultVariants: { size: "default", shape: "circle" },
});

interface TenkoAvatarSkeletonProps extends VariantProps<typeof avatarSkeletonVariants> {
  className?: string;
}

/**
 * Skeleton placeholder that matches `TenkoAvatar` dimensions and shape. Pass the same `size` and `shape`
 * used on the target avatar.
 *
 * @summary Avatar-shaped loading placeholder, matches TenkoAvatar sizes and shapes.
 */
export function TenkoAvatarSkeleton({ size, shape, className }: TenkoAvatarSkeletonProps) {
  return <Skeleton className={cn(avatarSkeletonVariants({ size, shape }), className)} />;
}

// ─── Field ────────────────────────────────────────────────────────────────────

interface TenkoFieldSkeletonProps {
  /** Render a label line above the input. Defaults to `true`. */
  label?: boolean;
  /** Render a description line below the input. Defaults to `false`. */
  description?: boolean;
  className?: string;
}

/**
 * Skeleton placeholder for a complete form field: optional label line, input area, and optional description
 * line. Use in form loading states to match the layout of `TenkoInputField` and similar field wrappers.
 *
 * @summary Form field loading placeholder with optional label and description lines.
 */
export function TenkoFieldSkeleton({
  label = true,
  description = false,
  className,
}: TenkoFieldSkeletonProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && <Skeleton className="h-4 w-24 rounded-md" />}
      <Skeleton className="h-9 w-full rounded-md" />
      {description && <Skeleton className="h-3.5 w-2/3 rounded-md" />}
    </div>
  );
}
